import { Formik, FormikHelpers } from "formik";
import { Page, useNotification } from "../../components";
import { AdminUI } from "../../ui/AdminUI";
import * as yup from 'yup';
import "yup-phone-lite";
import { createContext, useContext, useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { DISPLAYNAME_EXISTS_QUERY, HANDLE_EXISTS_QUERY } from "./queries";

import { useAuth } from "../../hooks";
import { QueryDisplayNameExistsArgs, QueryHandleExistsArgs, Seller, SetSellerInput } from '@shopify-multiseller/graphql/partner/types';
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import EmptyPage from "../EmptyPage/EmptyPage";
import ProfileList from "./SettingList";
import { SettingsRail } from "./SettingsRail";
import { settingsDestinations } from "./destinations";
import SettingListLoader from "./SettingListLoader";

interface ImageState {
    file: File | null;
    changed: boolean;
    removed: boolean;
}

export type StorefrontFormState = Omit<SetSellerInput, 'profileImage' | 'coverImage' | 'logo'> & {
    profileImage: ImageState;
    coverImage: ImageState;
    logo: ImageState;
};

interface StorefrontSettingsContextProps {
    seller: Seller | undefined;
    loading: boolean;
    updating: boolean;
    loadingDisplayNameExists: boolean;
    loadingHandleExists: boolean;
    handleAvailable?: boolean;
}

export const StorefrontSettingsContext = createContext<StorefrontSettingsContextProps | undefined>(
    undefined
);

export const useStorefrontSettings = () => {
    const context = useContext(StorefrontSettingsContext);
    if (!context) {
        throw new Error("useSettings must be used within a SettingsProvider");
    }
    return context;
};

interface FieldConfig {
    [key: string]: boolean;
}

/**
 * The rail beside the settings content. Its own component so the loading and
 * loaded branches place it identically — the destinations must not move when
 * the form arrives.
 */
const SettingsLayout: React.FC<
    React.PropsWithChildren<{
        destinations: ReturnType<typeof settingsDestinations>;
        /** Replaces the settings rail — the storefront page lists its own sections. */
        rail?: React.ReactNode;
    }>
> = ({ destinations, rail, children }) => (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[11rem_minmax(0,1fr)]">
        <div className="lg:sticky lg:top-20 lg:self-start">
            {rail ?? <SettingsRail destinations={destinations} />}
        </div>
        {/* A measure, not the whole page: settings fields stretched to 1200px
            put the label and its input at opposite ends of the screen. */}
        <div className="min-w-0 max-w-3xl">{children}</div>
    </div>
);

interface StorefrontSettingsProviderProps {
    children: React.ReactElement<React.PropsWithChildren>;
    title: string;
    fieldConfig: FieldConfig;
    actionTitle?: string;
    showFooterActions?: boolean;
    /** Replaces the settings rail for pages that list their own sections. */
    rail?: React.ReactNode;
}

export const StorefrontSettingsProvider = ({
    children,
    title,
    fieldConfig,
    actionTitle = "Settings",
    showFooterActions = true,
    rail,
}: React.PropsWithChildren<StorefrontSettingsProviderProps>) => {
    const notification = useNotification();
    const auth = useAuth();

    if (!auth) {
        throw new Error("Auth context not found");
    }

    const [formState, setFormState] = useState<StorefrontFormState | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);

    /** Whether the handle in the field is free. undefined until it has been checked. */
    const [handleAvailable, setHandleAvailable] = useState<boolean | undefined>(undefined);

    const [getHandleExists, { loading: loadingHandleExists }] = useLazyQuery<
        { handleExists: boolean },
        QueryHandleExistsArgs
    >(HANDLE_EXISTS_QUERY);

    const [getDisplayNameExists, { loading: loadingDisplayNameExists }] = useLazyQuery<
        { displayNameExists: boolean },
        QueryDisplayNameExistsArgs
    >(DISPLAYNAME_EXISTS_QUERY);

    const generateValidationSchema = (fieldConfig: FieldConfig) => {
        const schema: Record<string, yup.AnySchema> = {};

        if (fieldConfig.bio) {
            schema.bio = yup.string().nullable();
        }
        if (fieldConfig.storeName) {
            schema.storeName = yup.string().required('Store name is required');
        }
        if (fieldConfig.tagline) {
            schema.tagline = yup.string().nullable();
        }
        if (fieldConfig.notification) {
            schema.notification = yup.string().nullable();
        }
        if (fieldConfig.headline) {
            schema.headline = yup.string().nullable();
        }
        if (fieldConfig.story) {
            schema.story = yup.string().nullable();
        }
        if (fieldConfig.displayName) {
            schema.displayName = yup.string()
                .trim()
                .required('Display Name is required')
                .min(6, 'Display Name must be at least 6 characters')
                .max(20, 'Display Name must be at most 20 characters')
                .matches(/^[a-zA-Z0-9\s]*$/, 'Display Name can only contain alphanumeric characters and spaces')
                .test('displayName-exists', 'Display Name already exists', async function (value) {
                    // Manually recheck validations
                    if (value && value.trim().length >= 6 && value.trim().length <= 20 && /^[a-zA-Z0-9\s]*$/.test(value)) {
                        if (value.trim() === auth.user?.seller?.displayName) return true; // Skip validation if it matches the seller's current display name
                        try {
                            const isValid = await validateDisplayName(value.trim());
                            return isValid;
                        } catch (error) {
                            console.error(error);
                            return false;
                        }
                    } else {
                        // If manual checks do not pass, skip the async validation
                        return true;
                    }
                });
        }
        if (fieldConfig.handle) {
            schema.handle = yup.string()
                .trim()
                .required('Handle is required')
                .min(6, 'Handle must be at least 6 characters')
                .max(20, 'Handle must be at most 20 characters')
                .matches(/^[a-zA-Z0-9_]*$/, 'Handle can only contain alphanumeric characters and underscores')
                .test('handle-exists', 'Handle already exists', async function (value) {
                    // Manually recheck validations
                    if (value && value.trim().length >= 6 && value.trim().length <= 20 && /^[a-zA-Z0-9_]*$/.test(value)) {
                        if (value.trim() === auth.user?.seller?.handle) return true; // Skip validation if it matches the seller's current handle
                        try {
                            const isValid = await validateHandle(value.trim());
                            return isValid;
                        } catch (error) {
                            console.error(error);
                            return false;
                        }
                    } else {
                        // If manual checks do not pass, skip the async validation
                        return true;
                    }
                });
        }
        if (fieldConfig.firstName) {
            schema.firstName = yup.string()
                .trim()
                .required('First name is required')
                .matches(/^[a-zA-Z-]*$/, 'First name can only contain letters and hyphens');
        }
        if (fieldConfig.lastName) {
            schema.lastName = yup.string()
                .trim()
                .required('Last name is required')
                .matches(/^[a-zA-Z-]*$/, 'Last name can only contain letters and hyphens');
        }
        if (fieldConfig.phone) {
            schema.phone = yup.string()
                .trim()
                .phone("US", "Please enter a valid phone number")
                .required("A phone number is required");
        }
        if (fieldConfig.companyName) {
            schema.companyName = yup.string().trim().required('Company name is required');
        }

        return yup.object(schema);
    };

    const ValidationSchema = generateValidationSchema(fieldConfig);

    // useEffect to force handle to lowercase
    useEffect(() => {
        if (formState?.handle) {
            formState.handle = formState.handle.toLowerCase();
        }
    }, [formState?.handle]);

    useEffect(() => {
        if (auth.user?.seller) {
            const sellerFormState = populateFormState(auth.user.seller);
            setFormState(sellerFormState);
            // A seller's own saved handle is theirs, so it is available to
            // them. Without this the field only states availability after
            // something has been typed, and the loaded form — the state
            // anyone actually looks at — says nothing at all.
            setHandleAvailable(Boolean(auth.user.seller.handle));
            setLoading(false);
        } else {
            setError(`An error occurred while loading the ${actionTitle}.`);
            setLoading(false);
        }
    }, [auth.user?.seller, actionTitle]);

    const validateDisplayName = async (displayName: string): Promise<boolean> => {
        if (displayName.trim() === auth.user?.seller?.displayName) return true;
        const response = await getDisplayNameExists({ variables: { displayName: displayName } });
        if (response.data?.displayNameExists && response.data.displayNameExists === true) {
            return false;
        }
        return true;
    }

    const validateHandle = async (handle: string): Promise<boolean> => {
        // The answer is recorded, not just returned. Validation alone can only
        // say "taken" after a failed save; the design states availability on
        // the field while it is being typed, which needs the affirmative
        // result as well as the negative one.
        if (handle.trim() === auth.user?.seller?.handle) {
            setHandleAvailable(true);
            return true;
        }
        const response = await getHandleExists({ variables: { handle: handle } });
        if (response.data?.handleExists && response.data.handleExists === true) {
            setHandleAvailable(false);
            return false;
        }
        setHandleAvailable(true);
        return true;
    }

    const handleOnSubmit = async (
        values: StorefrontFormState,
        actions: FormikHelpers<StorefrontFormState>
    ) => {
        setUpdating(true);
        const sellerInput: SetSellerInput = {};
        const writable = sellerInput as Record<string, unknown>;
        Object.keys(values).forEach((key) => {
            writable[key] = fieldConfig[key] ? (values as Record<string, unknown>)[key] : undefined;
        });

        // Handle the special cases for images. File is the runtime
        // representation of the Upload scalar.
        const imageValue = (state: ImageState) =>
            state.removed ? null : ((state.file ?? undefined) as unknown as SetSellerInput['profileImage']);
        sellerInput.profileImage = fieldConfig.profileImage && values.profileImage.changed
            ? imageValue(values.profileImage)
            : undefined;
        sellerInput.coverImage = fieldConfig.coverImage && values.coverImage.changed
            ? imageValue(values.coverImage)
            : undefined;
        sellerInput.logo = fieldConfig.logo && values.logo.changed
            ? imageValue(values.logo)
            : undefined;

        try {
            const updatedSeller = await auth.updateSeller(sellerInput);
            if (updatedSeller) {
                setUpdating(false);
                actions.setSubmitting(false);
                const sellerFormState = populateFormState(updatedSeller);
                setFormState(sellerFormState);
                actions.resetForm({ values: sellerFormState });
                notification.success({
                    message: `Your ${actionTitle} has been updated successfully`,
                });
            } else {
                throw new Error("Failed to update seller");
            }
        } catch (error) {
            console.error(error);
            setUpdating(false);
            actions.setSubmitting(false);
            actions.resetForm();
            notification.error({
                message: `There was an error updating your ${actionTitle}`,
            });
        }
    };

    if (error) return <AdminUI><EmptyPage
        title={`Your ${actionTitle} didn't load`}
        description="The connection dropped or the server is busy. Nothing you have saved is affected."
        icon={ExclamationCircleIcon}
    />
    </AdminUI>;
    const destinations = settingsDestinations(auth.user?.seller?.settings?.storefront?.enabled);

    if (loading || !formState || !auth.user?.seller) return <>
        <AdminUI>
            <Page title={title}>
                <SettingsLayout destinations={destinations} rail={rail}>
                    <SettingListLoader />
                </SettingsLayout>
            </Page>
        </AdminUI>
    </>;

    return (
        <StorefrontSettingsContext.Provider
            value={{
                seller: auth.user?.seller,
                loading,
                updating,
                loadingDisplayNameExists,
                loadingHandleExists,
                handleAvailable,
            }}
        >
            {error ? (
                <AdminUI>
                    <EmptyPage
                        title="Something went wrong"
                        description={error}
                        icon={ExclamationCircleIcon}
                    />
                </AdminUI>
            ) : loading ? (
                <AdminUI>
                    <SettingListLoader />
                </AdminUI>
            ) : (
                <AdminUI>
                    <Formik
                        initialValues={formState!}
                        validationSchema={ValidationSchema}
                        onSubmit={handleOnSubmit}
                        enableReinitialize
                    >
                        {formikProps => (
                            <Page
                                title={title}
                                loading={updating}
                                // The same floating bar the product editor uses,
                                // rather than a Save button parked at the bottom
                                // of one section: settings is one form, and the
                                // control for it should follow you down it.
                                saveBar={
                                    showFooterActions
                                        ? {
                                              show: formikProps.dirty,
                                              message: 'Unsaved changes',
                                              messageStatus: 'info',
                                              saveAction: {
                                                  onAction: () => formikProps.handleSubmit(),
                                                  disabled: formikProps.isSubmitting,
                                                  loading: formikProps.isSubmitting,
                                              },
                                              discardAction: {
                                                  onAction: () => formikProps.handleReset(),
                                                  disabled: formikProps.isSubmitting,
                                                  loading: false,
                                              },
                                          }
                                        : undefined
                                }
                            >
                                <SettingsLayout destinations={destinations} rail={rail}>
                                    <ProfileList>{children}</ProfileList>
                                </SettingsLayout>
                            </Page>
                        )}
                    </Formik>
                </AdminUI>
            )}
        </StorefrontSettingsContext.Provider>
    );
};

const populateFormState = (seller: Seller): StorefrontFormState => ({
    about: seller.about,
    address: seller.address && {
        address1: seller.address?.address1,
        address2: seller.address?.address2,
        city: seller.address?.city,
        countryCode: seller.address?.countryCode,
        postCode: seller.address?.postCode,
        provinceCode: seller.address?.provinceCode,
    },
    companyName: seller.companyName,
    displayName: seller.displayName,
    firstName: seller.firstName,
    handle: seller.handle,
    lastName: seller.lastName,
    phone: seller.phone,
    bio: seller.bio || '',
    profileImage: { file: null, changed: false, removed: false },
    storeTitle: seller.storeTitle || '',
    ownerName: seller.ownerName || '',
    tagline: seller.tagline || '',
    coverImage: { file: null, changed: false, removed: false },
    logo: { file: null, changed: false, removed: false },
    notification: seller.notification || '',
    headline: seller.headline || '',
    story: seller.story || '',
});
